import React, { useState } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";

const Tooltip = ({ children, content }) => {
	const [isVisible, setIsVisible] = useState(false);

	return (
		<div
			className="relative inline-block"
			onMouseEnter={() => setIsVisible(true)}
			onMouseLeave={() => setIsVisible(false)}
		>
			{children}
			<AnimatePresence>
				{isVisible && (
					<motion.div
						initial={{ opacity: 0, y: 5 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: 5 }}
						className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-900 text-white text-sm rounded-lg whitespace-nowrap z-50"
					>
						{content}
						<div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900"></div>
					</motion.div>
				)}
			</AnimatePresence>
		</div>
	);
};

const IconWithTooltip = ({ src, alt, title }) => (
	<Tooltip content={title}>
		<img src={src} alt={alt} className="w-4 h-4 mx-1.5 cursor-help" />
	</Tooltip>
);

const FabricSeriesHeader = ({
	title,
	headerInfo,
	lg = false,
	className = "",
	...props
}) => {
	const country = useSelector((state) => state.country);

	const displayCountryPrice = (price) => {
		price = parseFloat(price);
		if (country === "USA") {
			return `$${(price * 1.5 * 0.75).toFixed(2)} USD`;
		} else {
			return `$${(price * 1.5).toFixed(2)} CAD`;
		}
	};

	if (headerInfo.ocs_name === "NULL") return null;

	return (
		<div
			className={`mt-4 w-full ${lg ? "p-4" : "p-2"} ${className}`}
			{...props}
		>
			<ul className="list-none m-0 p-0">
				<li className="flex flex-wrap items-baseline gap-2">
					{/* Series Name */}
					<span className="text-2xl font-semibold">{headerInfo.ocs_name}</span>

					{/* Price Information */}
					<span className="text-xl font-semibold text-gray-700">
						{headerInfo.allPrices?.priceGroupDifference &&
						headerInfo.included ? (
							<span className="text-green-600">(Included)</span>
						) : (
							headerInfo.allPrices?.priceGroupDifference[
								headerInfo.priceGroup
							] && (
								<span className="text-primary">
									{parseFloat(headerInfo.disc) !== 0.0
										? `+${displayCountryPrice(
												headerInfo.allPrices?.priceGroupDifference[
													headerInfo.priceGroup
												] *
													(1 - headerInfo.disc) *
													(1 - headerInfo.globalDisc)
										  )}`
										: `+${displayCountryPrice(
												headerInfo.allPrices?.priceGroupDifference[
													headerInfo.priceGroup
												] -
													headerInfo.allPrices?.priceGroupDifference[
														headerInfo.firstPG
													]
										  )}`}
								</span>
							)
						)}
					</span>

					{/* Fabric Group */}
					{headerInfo.group && (
						<Tooltip content="Fabric Group">
							<span className="text-xl px-2 cursor-help text-gray-600">
								{headerInfo.group}
							</span>
						</Tooltip>
					)}

					{/* Composition Info */}
					{headerInfo.composition && headerInfo.composition !== "NULL" && (
						<Tooltip content="Fabric Composition">
							<span className="text-xl px-2 cursor-help text-gray-600">
								{headerInfo.composition}
							</span>
						</Tooltip>
					)}

					{/* Feature Icons */}
					{(headerInfo.green_rating ||
						headerInfo.fire_rating ||
						headerInfo.antimicrobial_rating) && (
						<div className="inline-flex items-center gap-1">
							{headerInfo.green_rating && (
								<IconWithTooltip
									src="/images/icons/eco-friendly.png"
									alt="eco-friendly"
									title="Eco-Friendly / Green"
								/>
							)}
							{headerInfo.fire_rating && (
								<IconWithTooltip
									src="/images/icons/fire-resistant.png"
									alt="fire-resistant"
									title="Fire-Resistant"
								/>
							)}
							{headerInfo.antimicrobial_rating && (
								<IconWithTooltip
									src="/images/icons/antimicrobial.png"
									alt="antimicrobial"
									title="Antimicrobial"
								/>
							)}
						</div>
					)}
				</li>
			</ul>
		</div>
	);
};

export default FabricSeriesHeader;
